/**
 * Shared share-message form editor for the component-driven topic path.
 *
 * Edits ONE sharing config (the topic_garden dialect — `@/modules/form`,
 * gated by `topicGardenEnabled`) and hands back validated YAML. Every type is
 * edited the same way, news included: one SLOT STACK per intent over that
 * intent's whole form — a card per form element, typed by who fills it,
 * tap-to-expand to edit, every slot equally editable. Reassembled + validated
 * with `assembleSharingConfigYaml`.
 *
 * `injectableFields` are the type-specific tokens the author may drop into the
 * message; they're surfaced as an insertable-token palette.
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  IonActionSheet,
  IonLabel,
  IonSegment,
  IonSegmentButton,
} from '@ionic/react';
import { FooterDock, FooterDockRow, SmartCaseButton } from '@/modules/footer';
import {
  INTENT_NOUN,
  INTENT_SYMBOL,
  type MessageIntent,
  type TemplateField,
} from '@/modules/template';
import { FormLogic } from './form_logic';
import { SlotLogic } from './slot_logic';
import type { SlotLinkProps, SlotLinkTarget } from './SlotEditors';
import { SharingConfigLogic } from './sharing_config_logic';
import type { SlotType } from './slot_logic';
import { SlotStack, ADD_SLOT_TYPES } from './SlotStack';
import SharingSettingsPanel from './SharingSettingsPanel';
import {
  DEFAULT_STARTING_SENDER_APPOSITIVE,
  type ReshareStorySettings,
} from '@/modules/topic/types';
import { TopicLogic } from '@/modules/topic/logic';
import type { SharingFormSlot, SharingConfig } from './types';
import type { TopicTypeId } from '@/modules/topic/types';
import styles from './FormBuilder.module.css';

export interface FormBuilderProps {
  /** The share form to edit. */
  formYaml: string;
  /** Type-specific tokens the author can inject into the message. */
  injectableFields?: readonly TemplateField[];
  /**
   * The reassembled YAML, emitted as the author edits — there is no Done here.
   * The builder is a step inside the topic's edit flow, which owns the single
   * save, so an edit that assembles goes straight into that draft. An edit that
   * does not assemble (a slot with an outstanding warning) is shown in place and
   * withheld until it does, so the draft never holds an unusable form.
   */
  onChange: (yaml: string) => void;
  /**
   * The Settings tab's draft, or null when the topic has never saved it. Like
   * the forms, it belongs to the topic's edit flow: edits go straight to
   * `onReshareStorySettingsChange` and that flow's save stores them.
   */
  reshareStorySettings?: ReshareStorySettings | null;
  onReshareStorySettingsChange?: (settings: ReshareStorySettings) => void;
  /**
   * How the story describes the starting sender while the Settings tab has
   * picked no description: the topic type's default.
   */
  defaultStartingSenderAppositive?: string;
  /**
   * The type's default form. Reverting puts one intent back against it, so the
   * builder needs the untouched original alongside the seed it is editing
   * (which may already be a saved custom form).
   */
  defaultFormYaml: string;
  /**
   * Whether the topic (as drafted) sets an impression goal. Gates the `goal`
   * slot: the add sheet only offers one while a goal exists, and an existing
   * goal slot renders disabled once the goal is cleared.
   */
  hasImpressionGoal?: boolean;
  /**
   * The topic's type, whose definitions (its `definitions.yaml`) the forms
   * may read; with none, any type's.
   */
  topicType?: TopicTypeId;
  /**
   * Whether the topic holds a saved custom form the caller could not seed the
   * builder with — a payload in a format this dialect no longer parses — so
   * what is on screen is the type default standing in for it.
   *
   * The revert control keys off this as well as off the edits on screen.
   * Comparing the seed against the default answers "is there anything to put
   * back?" only while the seed IS what the topic saved; when it is the stand-in,
   * every form matches the default and the control would hide itself in the one
   * case it is most needed — the unusable payload is still saved on the topic,
   * and reverting is how the author replaces it.
   */
  savedFormUnusable?: boolean;
}

/** Read-only palette of the type-specific tokens the author can inject. */
const InjectableFieldsPalette: React.FC<{
  fields: readonly TemplateField[];
}> = ({ fields }) => {
  if (fields.length === 0) return null;
  return (
    <div className={styles.palette} data-testid="form-builder-injectables">
      <div className={styles.fieldLabel}>Insertable fields</div>
      <div className={styles.paletteTokens}>
        {fields.map(f => (
          <span
            key={f.id}
            title={f.help ?? f.label}
            className={styles.paletteToken}
          >
            {`{${f.id}}`}
          </span>
        ))}
      </div>
    </div>
  );
};

/**
 * The builder's dock. "Add slot" is the only thing an author does here often
 * enough to earn the primary CTA; reverting the form on screen is rare and
 * destructive, so it sits below as a secondary and confirms first.
 */
const BuilderDock: React.FC<{
  onAddSlot?: () => void;
  /** Off while the open card still needs its prompt, since adding closes it. */
  canAddSlot?: boolean;
  onRevert?: () => void;
  /** Names what revert will replace, when it replaces one form of several. */
  revertTitle?: string;
}> = ({ onAddSlot, canAddSlot = true, onRevert, revertTitle }) => {
  const [confirmRevert, setConfirmRevert] = useState(false);
  return (
    <FooterDock background="opaque">
      {onAddSlot && (
        <FooterDockRow landmark>
          <SmartCaseButton
            expand="block"
            color="primary"
            className={styles.dockButton}
            onClick={onAddSlot}
            disabled={!canAddSlot}
            data-testid="form-builder-add-slot"
          >
            ＋ Add slot
          </SmartCaseButton>
        </FooterDockRow>
      )}
      {onRevert && (
        <FooterDockRow landmark={!onAddSlot}>
          <SmartCaseButton
            expand="block"
            fill="outline"
            color="medium"
            className={styles.dockButton}
            onClick={() => setConfirmRevert(true)}
            data-testid="form-builder-revert"
          >
            Revert to default
          </SmartCaseButton>
        </FooterDockRow>
      )}
      <IonActionSheet
        isOpen={confirmRevert}
        onDidDismiss={() => setConfirmRevert(false)}
        header={
          revertTitle
            ? `Revert the ${revertTitle} to default?`
            : 'Revert to the default form?'
        }
        data-testid="form-builder-revert-sheet"
        buttons={[
          { text: 'Revert to default', role: 'destructive', handler: onRevert },
          { text: 'Cancel', role: 'cancel' },
        ]}
      />
    </FooterDock>
  );
};

/** The Settings tab's key, alongside the intents that key the form tabs. */
const SETTINGS_TAB = 'settings';

/**
 * Drops the tabs' intent emoji once they cost a label its full name.
 *
 * Whether four labels fit depends on the Ionic mode (md sets them uppercase and
 * letter-spaced) and on the device's fonts as much as on the screen width, so
 * no breakpoint could say it. Instead the labels are measured with their emoji
 * in place, and the emoji go whenever any label would be cut off. The flag is a
 * data attribute on a wrapper React never rewrites, so the check reads and
 * writes layout in one pass without a render in between.
 */
function useSymbolsWhileTheyFit(ref: React.RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const check = () => {
      delete el.dataset.compact;
      const clipped = Array.from(
        el.querySelectorAll<HTMLElement>('ion-label')
      ).some(label => label.scrollWidth > label.clientWidth);
      if (clipped) el.dataset.compact = 'true';
    };
    // The labels are observed as well as the wrapper: they settle to their
    // real width only once the segment buttons hydrate, which does not resize
    // the wrapper. Hiding the emoji never resizes a label — each takes an equal
    // share of the segment — so the check cannot retrigger itself.
    const ro = new ResizeObserver(check);
    ro.observe(el);
    el.querySelectorAll('ion-label').forEach(label => ro.observe(label));
    void document.fonts?.ready.then(check);
    return () => ro.disconnect();
  }, [ref]);
}

/**
 * Which tab is showing: one per intent's form, then Settings. The segment names
 * every tab at once, so the author sees what the setup holds and picks one
 * directly.
 */
const BuilderTabs: React.FC<{
  intents: readonly MessageIntent[];
  active: string;
  /** Keeps the author on the active tab, while its open card is unfinished. */
  locked?: boolean;
  onSelect: (tab: string) => void;
}> = ({ intents, active, locked = false, onSelect }) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  useSymbolsWhileTheyFit(wrapRef);
  return (
    <div ref={wrapRef} className={styles.tabsWrap}>
      <IonSegment
        value={active}
        onIonChange={e => {
          if (locked) return;
          if (typeof e.detail.value === 'string') onSelect(e.detail.value);
        }}
        data-testid="form-builder-tabs"
      >
        {intents.map(intent => (
          <IonSegmentButton
            key={intent}
            value={intent}
            disabled={locked && intent !== active}
            className={styles.tab}
            data-testid={`form-builder-tab-${intent}`}
          >
            <IonLabel className={styles.tabLabel}>
              <span className={styles.tabSymbol} aria-hidden="true">
                {INTENT_SYMBOL[intent]}{' '}
              </span>
              {INTENT_NOUN[intent].one}
            </IonLabel>
          </IonSegmentButton>
        ))}
        <IonSegmentButton
          value={SETTINGS_TAB}
          disabled={locked && active !== SETTINGS_TAB}
          className={styles.tab}
          data-testid={`form-builder-tab-${SETTINGS_TAB}`}
        >
          <IonLabel className={styles.tabLabel}>Settings</IonLabel>
        </IonSegmentButton>
      </IonSegment>
    </div>
  );
};

/** How an intent's form is named where the author picks one to link into. */
function linkTarget(intent: MessageIntent): SlotLinkTarget {
  return {
    intent,
    label: `${INTENT_SYMBOL[intent]} ${INTENT_NOUN[intent].one}`,
  };
}

/**
 * Sharing-form slot editor: one slot stack per intent over that intent's whole
 * form — a single flat list of slots in form order, every one of them
 * editable. A slot is named by its id, unique across every form, so the one
 * open card is named by its slot's id alone and stays the same card however
 * the stacks change around it.
 */
const SharingFormEditor: React.FC<{
  config: SharingConfig;
  defaultFormYaml: string;
  onChange: (yaml: string) => void;
  hasImpressionGoal: boolean;
  topicType?: TopicTypeId;
  savedFormUnusable: boolean;
  reshareStorySettings: ReshareStorySettings | null;
  onReshareStorySettingsChange: (settings: ReshareStorySettings) => void;
  defaultStartingSenderAppositive: string;
}> = ({
  config,
  defaultFormYaml,
  onChange,
  hasImpressionGoal,
  topicType,
  savedFormUnusable,
  reshareStorySettings,
  onReshareStorySettingsChange,
  defaultStartingSenderAppositive,
}) => {
  const initialForms = useMemo(
    () => config.forms.map(f => f.form as readonly SharingFormSlot[]),
    [config]
  );
  const [forms, setForms] =
    useState<readonly (readonly SharingFormSlot[])[]>(initialForms);
  const [openId, setOpenId] = useState<string | null>(null);
  const [assembleError, setAssembleError] = useState<string | null>(null);
  const [tab, setTab] = useState<string>(
    () => config.forms[0]?.intent ?? SETTINGS_TAB
  );
  const [addOpen, setAddOpen] = useState(false);

  // The config as the forms stand, edits and all.
  const draftConfig = useMemo(
    () => SharingConfigLogic.updateSlots(config, forms),
    [forms, config]
  );

  // Read from the forms as they stand, so a variable the author has just typed
  // into one slot is offered by the picker on every other.
  const variableNames = useMemo(
    () => FormLogic.variableNames(draftConfig),
    [draftConfig]
  );

  // -1 while Settings is showing: no form is on screen to add to or revert.
  const tabIndex = config.forms.findIndex(f => f.intent === tab);
  const activeIntent = config.forms[tabIndex]?.intent;

  /**
   * Take an edit: hold it, and hand the reassembled form to the draft.
   *
   * Only an author's edit reaches the draft — seeding the builder does not.
   * Merely opening the builder must not mark the topic as customized, and after
   * a revert clears the draft the builder must not put its own seed straight
   * back.
   *
   * Validation gates gently: a slot with a problem still collapses and keeps its
   * warning, and the form is withheld from the draft until the warning clears,
   * so the pending save never holds an unusable form. Nothing traps the author
   * here — leaving is always allowed.
   */
  const applyEdit = useCallback(
    (edited: readonly (readonly SharingFormSlot[])[]) => {
      // Deleting or reverting away the other half of a link leaves the
      // remaining slot linked to nothing, so it stops saying it is linked.
      const next = SharingConfigLogic.withoutLoneLinks(
        SharingConfigLogic.updateSlots(config, edited)
      ).forms.map(f => f.form);
      setForms(next);
      if (
        next.flatMap(slots => SlotLogic.stackWarnings(slots, topicType))
          .length > 0
      ) {
        setAssembleError(null);
        return;
      }
      const result = FormLogic.assembleSharingConfigYaml(
        config,
        next,
        topicType
      );
      if (result.ok) {
        setAssembleError(null);
        onChange(result.yaml);
      } else {
        setAssembleError(result.error);
      }
    },
    [config, onChange, topicType]
  );

  /**
   * What the screen says is outstanding.
   *
   * Derived rather than remembered, so it answers to the stack as it stands —
   * committing a card the author left invalid has to surface it, and that is a
   * change of which card is open, not an edit.
   *
   * The open card is left out: it reports on itself, and adding a slot would
   * otherwise answer the click with an error about the slot not being filled in
   * yet. So are the slots linked to it, which read as it does and so carry the
   * same warning. Any other linked set is reported once, by its first member.
   */
  const error = useMemo(() => {
    if (assembleError) return assembleError;
    const openSlot =
      openId === null
        ? undefined
        : SharingConfigLogic.slotWithId(draftConfig, openId);
    const openLinkId = openSlot ? SlotLogic.linkIdOf(openSlot) : null;
    const reportedLinks = new Set(openLinkId === null ? [] : [openLinkId]);
    const elsewhere = forms.flatMap(slots =>
      slots.flatMap(slot => {
        if (slot.id === openId) return [];
        const linkId = SlotLogic.linkIdOf(slot);
        if (linkId !== null) {
          if (reportedLinks.has(linkId)) return [];
          reportedLinks.add(linkId);
        }
        const warning = SlotLogic.warningInStack(slots, slot.id, topicType);
        return warning ? [warning] : [];
      })
    );
    return elsewhere.length > 0
      ? `Not saved yet:\n${elsewhere.map(w => `• ${w}`).join('\n')}`
      : null;
  }, [assembleError, draftConfig, forms, openId, topicType]);

  // The type default, kept alongside the seed so one intent can be put back
  // without disturbing the other two. Forms are keyed by intent rather than by
  // position: a custom config is free to carry its intents in another order.
  const defaultForms = useMemo(() => {
    try {
      const parsed = FormLogic.parseSharingConfigRaw(
        defaultFormYaml,
        topicType
      );
      return new Map(
        parsed.forms.map(f => [f.intent, f.form as readonly SharingFormSlot[]])
      );
    } catch {
      return new Map<string, readonly SharingFormSlot[]>();
    }
  }, [defaultFormYaml, topicType]);

  // Only the form on screen can be reverted, and only when reverting it would
  // actually change what the topic holds — otherwise the control would promise
  // a change it cannot make. That is either an edit on screen, or a saved form
  // the builder could not load: the stand-in on screen matches the default
  // while the topic still holds the payload the author wants gone, and putting
  // this form back rewrites the whole config, taking the unusable payload with
  // it. The other intents' customizations are left exactly as they are.
  const activeDefault = activeIntent
    ? defaultForms.get(activeIntent)
    : undefined;
  const canRevert =
    activeDefault !== undefined &&
    (savedFormUnusable ||
      JSON.stringify(forms[tabIndex] ?? []) !== JSON.stringify(activeDefault));

  const handleRevertIntent = useCallback(() => {
    if (!activeDefault) return;
    applyEdit(
      forms.map((form, formIndex) =>
        formIndex === tabIndex ? activeDefault : form
      )
    );
    setOpenId(null);
  }, [activeDefault, applyEdit, forms, tabIndex]);

  // A Select or Fill in card stays open until it has a prompt, so nothing that
  // would close it or leave it (adding a slot, opening another card, switching
  // tabs) is offered meanwhile.
  const openSlot =
    openId === null
      ? undefined
      : forms
          .map(slots => SlotLogic.slotWithId(slots, openId))
          .find(slot => slot !== undefined);
  const openSlotLacksPrompt =
    openSlot !== undefined && SlotLogic.lacksPrompt(openSlot);

  // Switching tabs closes the open card, so the stack the author comes back to
  // reads as a list again.
  const handleSelectTab = useCallback((next: string) => {
    setTab(next);
    setOpenId(null);
  }, []);

  // Adding lands on the form the author is looking at, at the end of its stack,
  // and opens the new card so the next thing to do is fill it in.
  const handleAddSlot = useCallback(
    (type: SlotType) => {
      const added = SlotLogic.blankSlot(type);
      applyEdit(
        forms.map((form, formIndex) =>
          formIndex === tabIndex
            ? SlotLogic.insertSlot(form, form.length, added)
            : form
        )
      );
      setOpenId(added.id);
    },
    [applyEdit, forms, tabIndex]
  );

  /**
   * The soft-link controls for one slot. Linking copies the slot into each
   * newly ticked intent's form and leaves the author on the card they were
   * editing; the copies show up on their own tabs, and every linked card says
   * so.
   */
  const linkFor = useCallback(
    (slotId: string): SlotLinkProps | undefined => {
      const home = SharingConfigLogic.intentOf(draftConfig, slotId);
      if (home === undefined) return undefined;
      return {
        targets: draftConfig.forms.map(f => linkTarget(f.intent)),
        home,
        linkedWith: SharingConfigLogic.linkedPeers(
          draftConfig,
          slotId
        ).flatMap(peer => {
          const intent = SharingConfigLogic.intentOf(draftConfig, peer);
          return intent ? [linkTarget(intent)] : [];
        }),
        onSetLinks: intents =>
          applyEdit(
            SharingConfigLogic.setLinkedIntents(
              draftConfig,
              slotId,
              intents
            ).forms.map(f => f.form)
          ),
      };
    },
    [applyEdit, draftConfig]
  );

  return (
    <div className={styles.editorFrame}>
      {error && (
        <pre data-testid="form-builder-error" className={styles.error}>
          {error}
        </pre>
      )}
      <BuilderTabs
        intents={config.forms.map(f => f.intent)}
        active={tab}
        locked={openSlotLacksPrompt}
        onSelect={handleSelectTab}
      />
      {/*
        One panel per tab, only the selected one shown. The others stay mounted
        so each keeps its own scroll position, and each scrolls vertically on
        its own so the tabs and the footer stay put.
      */}
      <div className={styles.tabPanels}>
        {config.forms.map((f, j) => (
          <div
            key={f.intent}
            hidden={f.intent !== tab}
            className={styles.tabPanel}
            data-testid={`form-builder-panel-${f.intent}`}
          >
            <SlotStack
              slots={forms[j] ?? []}
              hasImpressionGoal={hasImpressionGoal}
              topicType={topicType}
              variableNames={variableNames}
              // The slot the Settings tab takes the allowed detail from,
              // while it is included; its card carries an "Allowed detail"
              // badge.
              allowedDetailSlotId={TopicLogic.allowedDetailSlotId(
                reshareStorySettings?.allowedDetailSettings,
                f.intent
              )}
              editingId={openId}
              onEditingIdChange={setOpenId}
              // An edit to the open card reaches every slot linked to it.
              onChange={next =>
                applyEdit(
                  SharingConfigLogic.carryEdit(
                    draftConfig,
                    SharingConfigLogic.updateSlots(
                      draftConfig,
                      forms.map((form, formIndex) =>
                        formIndex === j ? next : form
                      )
                    ),
                    openId
                  ).forms.map(f => f.form)
                )
              }
              testIdPrefix={`form-builder-stack-${f.intent}`}
              linkFor={linkFor}
            />
          </div>
        ))}
        <div
          hidden={tab !== SETTINGS_TAB}
          className={styles.tabPanel}
          data-testid={`form-builder-panel-${SETTINGS_TAB}`}
        >
          <SharingSettingsPanel
            introSlots={
              forms[config.forms.findIndex(f => f.intent === 'intro')] ?? []
            }
            settings={reshareStorySettings}
            onChange={onReshareStorySettingsChange}
            defaultStartingSenderAppositive={defaultStartingSenderAppositive}
          />
        </div>
      </div>
      <IonActionSheet
        isOpen={addOpen}
        onDidDismiss={() => setAddOpen(false)}
        header="Add a slot"
        data-testid="form-builder-add-sheet"
        buttons={[
          ...ADD_SLOT_TYPES.filter(type =>
            SlotLogic.offersTypeForInsertion(type, {
              hasImpressionGoal,
              variableNames,
            })
          ).map(type => ({
            text: SlotLogic.typeName(type),
            handler: () => handleAddSlot(type),
          })),
          { text: 'Cancel', role: 'cancel' as const },
        ]}
      />
      {activeIntent && (
        <BuilderDock
          onAddSlot={() => setAddOpen(true)}
          canAddSlot={!openSlotLacksPrompt}
          onRevert={canRevert ? handleRevertIntent : undefined}
          revertTitle={FormLogic.intentFormTitle(activeIntent)}
        />
      )}
    </div>
  );
};

const FormBuilder: React.FC<FormBuilderProps> = ({
  formYaml,
  injectableFields = [],
  onChange,
  defaultFormYaml,
  hasImpressionGoal = false,
  topicType,
  savedFormUnusable = false,
  reshareStorySettings = null,
  onReshareStorySettingsChange = () => {},
  defaultStartingSenderAppositive = DEFAULT_STARTING_SENDER_APPOSITIVE,
}) => {
  const config = useMemo(() => {
    try {
      return FormLogic.parseSharingConfigRaw(formYaml, topicType);
    } catch (err) {
      return err instanceof Error ? err.message : String(err);
    }
  }, [formYaml, topicType]);

  // A config is normally an object: the bundle of forms this topic shares with.
  // A string is the other case — the seed did not parse, and the string holds
  // the error message to show. Callers seed the builder with a form they have
  // already checked, falling back to the type default (which always parses), so
  // an unreadable seed means a shipped form is broken. Say so rather than
  // rendering an empty editor whose first edit would overwrite whatever the
  // topic had saved.
  if (typeof config === 'string') {
    return (
      <div data-testid="form-builder" className={styles.page}>
        <pre data-testid="form-builder-error" className={styles.error}>
          {config}
        </pre>
      </div>
    );
  }

  return (
    <div data-testid="form-builder" className={styles.frame}>
      <InjectableFieldsPalette fields={injectableFields} />
      <SharingFormEditor
        config={config}
        defaultFormYaml={defaultFormYaml}
        onChange={onChange}
        hasImpressionGoal={hasImpressionGoal}
        topicType={topicType}
        savedFormUnusable={savedFormUnusable}
        reshareStorySettings={reshareStorySettings}
        onReshareStorySettingsChange={onReshareStorySettingsChange}
        defaultStartingSenderAppositive={defaultStartingSenderAppositive}
      />
    </div>
  );
};

export default FormBuilder;
